// ============================================================
// The rows of an enriched hit's damage ledger in the structured
// combat log: dice, every bonus that added to them, armour,
// penetration, Toughness, the result, and Righteous Fury.
//
// A TABLE rather than a column of conditionals in the component: each
// row says when it appears and what it reads, so adding a bonus to
// the ledger is one entry here and the renderer never changes. The
// order of the table is the order on screen.
// ============================================================
import type { DamageBreakdown } from './structuredNarrative';

/** One ledger row as the log draws it; no `color` means the default ink. */
export interface LedgerRow {
  label: string;
  value: string;
  color?: string;
}

interface RowSpec {
  label: string;
  when: (d: DamageBreakdown) => boolean;
  value: (d: DamageBreakdown) => string;
  color?: (d: DamageBreakdown) => string;
}

/** A signed figure for a ledger row: +2, −2. */
const signed = (n: number) => (n > 0 ? '+' + n : String(n));

const positive = (n: number | undefined) => (n ?? 0) > 0;
const nonZero = (n: number | undefined) => n != null && n !== 0;

/** "2 base + 1 machine = 3", or the bare AP when no share is itemised. */
export function buildArmourStr(d: DamageBreakdown): string {
  const parts: string[] = [];
  if (d.baseAP) parts.push(d.baseAP + ' base');
  if (d.machineAP) parts.push(d.machineAP + ' machine');
  if (d.naturalAP) parts.push(d.naturalAP + ' natural');
  if (d.coverAP) parts.push(d.coverAP + ' cover');
  const total = (d.baseAP || 0) + (d.machineAP || 0) + (d.naturalAP || 0) + (d.coverAP || 0);
  const str = parts.length > 0 ? parts.join(' + ') + ' = ' + total : String(d.ap);
  // Says why the plate is worth less than the sheet claims. Reported on
  // the armour line because the cap is spent against armour first; when
  // it reached Toughness too, the TB line shows its own share.
  return d.soakCapReduction ? str + ' (soak cap −' + d.soakCapReduction + ')' : str;
}

/** "4 TB × 2 UT (Felling -1) +2 Daemonic = 9". */
export function buildTBStr(d: DamageBreakdown): string {
  const base = d.baseTB ?? d.tb;
  const ut = d.utMultiplier ?? 1;
  let s = base + ' TB';
  if (ut > 1) s += ' × ' + ut + ' UT';
  if (d.fellingReduction) s += ' (Felling -' + d.fellingReduction + ')';
  if (d.daemonicProt) s += ' +' + d.daemonicProt + ' Daemonic';
  s += ' = ' + d.tb;
  return s;
}

function righteousFuryStr(d: DamageBreakdown): string {
  return (
    'd100=' +
    d.rfRoll +
    (d.rfConfirmed ? ' CONFIRMED Sev ' + d.rfSeverity : ' unconfirmed') +
    (d.rfAutoConfirmed ? ' (Deathwatch Training vs Xenos)' : '')
  );
}

function penetrationStr(d: DamageBreakdown): string {
  return d.basePen + (d.effectivePen !== d.basePen ? ' → ' + d.effectivePen + ' effective' : '');
}

/** A bonus row that appears only when the bonus added something: "+3". */
function bonusRow(label: string, pick: (d: DamageBreakdown) => number | undefined, unit = ''): RowSpec {
  return { label, when: (d) => positive(pick(d)), value: (d) => '+' + pick(d) + unit };
}

/** An exposure row that appears only when the exposure changed something, signed. */
function exposureRow(label: string, pick: (d: DamageBreakdown) => number | undefined, unit = ''): RowSpec {
  return { label, when: (d) => nonZero(pick(d)), value: (d) => signed(pick(d) as number) + unit };
}

// Declarative: one entry per ledger row, in screen order.
const ROWS: RowSpec[] = [
  {
    label: 'FF Roll',
    when: (d) => positive(d.ffRoll) && !d.ffBlocked,
    value: (d) => 'd100=' + d.ffRoll + ' vs ' + (d.ffProtection ?? '?') + ' - passed through',
    color: () => '#60a5fa',
  },
  { label: 'Dice', when: (d) => !!d.diceStr, value: (d) => d.diceStr + ' = ' + d.raw },
  bonusRow('Strength', (d) => d.strengthBonus),
  bonusRow('Talent Bonus', (d) => d.talentDmgBonus),
  bonusRow('Accurate', (d) => d.accurateDice, 'd10'),
  bonusRow('Devastating', (d) => d.devastatingBonus),
  bonusRow('Maximal', (d) => d.maximalDmgBonus),
  {
    label: 'Scatter',
    when: (d) => nonZero(d.scatterMod),
    value: (d) => ((d.scatterMod as number) > 0 ? '+' : '') + d.scatterMod,
  },
  bonusRow('Force', (d) => d.forceBonus),
  bonusRow('Against the foe', (d) => d.predicateBonus),
  exposureRow('Exposed (damage)', (d) => d.exposureDmg),
  exposureRow('Exposed (penetration)', (d) => d.exposurePen),
  exposureRow('Exposed (toughness)', (d) => d.exposureTB),
  { label: 'Exposed (damage cap)', when: (d) => positive(d.exposureDmgCap), value: (d) => String(d.exposureDmgCap) },
  exposureRow('Exposed (critical)', (d) => d.exposureCrit, ' severity'),
  { label: 'Armour', when: (d) => d.baseAP != null, value: buildArmourStr },
  { label: 'Penetration', when: (d) => d.basePen != null, value: penetrationStr },
  { label: 'Effective AP', when: () => true, value: (d) => String(d.ap) },
  { label: 'Toughness', when: (d) => d.baseTB != null, value: buildTBStr },
  {
    label: 'Effective Dmg',
    when: () => true,
    value: (d) => String(d.effective),
    color: (d) => (d.effective > 0 ? '#ef4444' : '#6b7280'),
  },
  {
    label: 'Righteous Fury',
    when: (d) => !!d.righteousFury,
    value: righteousFuryStr,
    color: (d) => (d.rfConfirmed ? '#f59e0b' : '#555'),
  },
];

/** The ledger rows this hit shows, in order. Effective AP and damage always appear. */
export function damageLedgerRows(d: DamageBreakdown): LedgerRow[] {
  return ROWS.filter((row) => row.when(d)).map((row) => ({
    label: row.label,
    value: row.value(d),
    ...(row.color ? { color: row.color(d) } : {}),
  }));
}
